import { supabase } from './supabase'

export type ActivityAction =
  | 'login'
  | 'logout'
  | 'page_view'
  | 'order_placed'
  | 'cart_updated'
  | 'wishlist_updated'
  | 'vehicle_registered'
  | 'tow_request_created'
  | 'claim_filed'
  | 'vendor_assignment_updated'
  | 'parts_order_viewed'

export interface ActivityDetails {
  order_id?: string
  part_id?: string
  vendor_id?: string
  quantity?: number
  amount?: number
  status?: string
  [key: string]: any
}

/**
 * Log user activity asynchronously (non-blocking)
 * This won't slow down the main application flow
 */
export async function logActivity(
  action: ActivityAction,
  page?: string,
  details?: ActivityDetails
) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      console.log('Activity log: No user logged in')
      return
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('role, email, full_name, phone')
      .eq('id', user.id)
      .single()

    console.log('Logging activity:', { action, page, details, profile })

    // Fire and forget - don't await to avoid blocking
    supabase.from('activity_logs').insert({
      user_id: user.id,
      user_email: profile?.email || user.email,
      user_role: profile?.role || 'unknown',
      action,
      page,
      details: {
        ...details,
        name: profile?.full_name,
        phone: profile?.phone
      },
      ip_address: '', // Could be extracted from headers on server
      user_agent: navigator.userAgent
    }).then(() => {
      console.log('Activity logged successfully:', action)
    }).catch((err: unknown) => {
      // Silent fail - logging errors shouldn't break the app
      console.error('Activity log error:', err)
    })
  } catch (error) {
    // Silent fail
    console.error('Activity log error:', error)
  }
}

/**
 * Log page view (call this on route changes)
 */
export function logPageView(page: string) {
  logActivity('page_view', page)
}

/**
 * Log login
 */
export function logLogin() {
  logActivity('login', '/dashboard')
}

/**
 * Log logout
 */
export function logLogout() {
  logActivity('logout')
}
